import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import { Mark, mergeAttributes } from '@tiptap/core'
import { useState, useCallback, useEffect, useRef } from 'react'
import {
    Sparkles, Loader2, RefreshCw, Copy, Share2, Download,
    Check, X, Twitter, Linkedin, Facebook, Mail, FileText,
    FileDown, Maximize2, Minimize2, Type, Zap, BookOpen,
    Clock, AlertTriangle, CheckCircle2, ChevronDown, Wand2,
    Bold, Italic, Underline as UnderlineIcon, Strikethrough,
    Heading1, Heading2, Heading3, List, ListOrdered, Quote,
    Undo, Redo, AlignLeft, AlignCenter, AlignRight, Minus,
    FileType
} from 'lucide-react'
import { detectWithAI, checkRateLimitAsync, incrementRateLimit, checkRateLimit } from '../../utils/gemini'
import html2pdf from 'html2pdf.js'

// Custom Cliche Highlight Mark for TipTap
const ClicheHighlight = Mark.create({
    name: 'clicheHighlight',

    addAttributes() {
        return {
            severity: {
                default: 'medium',
                parseHTML: element => element.getAttribute('data-severity'),
                renderHTML: attributes => ({
                    'data-severity': attributes.severity,
                }),
            },
            suggestion: {
                default: '',
                parseHTML: element => element.getAttribute('data-suggestion'),
                renderHTML: attributes => ({
                    'data-suggestion': attributes.suggestion,
                }),
            },
            phrase: {
                default: '',
                parseHTML: element => element.getAttribute('data-phrase'),
                renderHTML: attributes => ({
                    'data-phrase': attributes.phrase,
                }),
            },
        }
    },

    parseHTML() {
        return [{ tag: 'span[data-cliche]' }]
    },

    renderHTML({ HTMLAttributes }) {
        const severity = HTMLAttributes['data-severity'] || 'medium'
        const classes = {
            high: 'cliche-mark cliche-high',
            medium: 'cliche-mark cliche-medium',
            low: 'cliche-mark cliche-low',
        }
        return ['span', mergeAttributes(HTMLAttributes, {
            'data-cliche': 'true',
            class: classes[severity] || classes.medium
        }), 0]
    },
})

// Comprehensive cliché patterns
const CLICHE_PATTERNS = [
    // Business/Corporate clichés
    { phrase: 'think outside the box', severity: 'high', suggestion: 'approach creatively' },
    { phrase: 'at the end of the day', severity: 'high', suggestion: 'ultimately' },
    { phrase: 'low-hanging fruit', severity: 'high', suggestion: 'easy wins' },
    { phrase: 'game-changing', severity: 'high', suggestion: 'transformative' },
    { phrase: 'game-changer', severity: 'high', suggestion: 'transformative' },
    { phrase: 'paradigm shift', severity: 'high', suggestion: 'fundamental change' },
    { phrase: 'move the needle', severity: 'medium', suggestion: 'make progress' },
    { phrase: 'circle back', severity: 'medium', suggestion: 'revisit' },
    { phrase: 'deep dive', severity: 'medium', suggestion: 'thorough analysis' },
    { phrase: 'synergy', severity: 'medium', suggestion: 'collaboration' },
    { phrase: 'touch base', severity: 'medium', suggestion: 'connect' },
    { phrase: 'bandwidth', severity: 'medium', suggestion: 'capacity' },
    { phrase: 'leverage', severity: 'medium', suggestion: 'use' },
    { phrase: 'take it to the next level', severity: 'high', suggestion: 'improve significantly' },
    { phrase: 'cutting-edge', severity: 'high', suggestion: 'advanced' },
    { phrase: 'state-of-the-art', severity: 'high', suggestion: 'modern' },
    { phrase: 'best-in-class', severity: 'high', suggestion: 'excellent' },
    { phrase: 'world-class', severity: 'medium', suggestion: 'excellent' },
    { phrase: 'robust', severity: 'low', suggestion: 'strong' },
    { phrase: 'seamless', severity: 'medium', suggestion: 'smooth' },
    { phrase: 'streamline', severity: 'medium', suggestion: 'simplify' },
    { phrase: 'empower', severity: 'medium', suggestion: 'enable' },
    { phrase: 'unlock the full potential', severity: 'high', suggestion: 'maximize capabilities' },
    { phrase: 'unlock potential', severity: 'high', suggestion: 'enable growth' },
    { phrase: 'ahead of the curve', severity: 'high', suggestion: 'leading' },
    { phrase: 'one-stop shop', severity: 'high', suggestion: 'complete solution' },
    { phrase: 'hit the ground running', severity: 'high', suggestion: 'start quickly' },
    { phrase: 'boots on the ground', severity: 'high', suggestion: 'field presence' },
    { phrase: 'future-proof', severity: 'high', suggestion: 'adaptable' },
    { phrase: 'revolutionary', severity: 'high', suggestion: 'innovative' },
    { phrase: 'groundbreaking', severity: 'high', suggestion: 'pioneering' },
    // Common writing clichés
    { phrase: 'a double-edged sword', severity: 'high', suggestion: 'has both benefits and drawbacks' },
    { phrase: 'in this day and age', severity: 'high', suggestion: 'today' },
    { phrase: 'only time will tell', severity: 'high', suggestion: 'the outcome is uncertain' },
    { phrase: 'the tip of the iceberg', severity: 'high', suggestion: 'just the beginning' },
    { phrase: 'easier said than done', severity: 'high', suggestion: 'challenging to implement' },
    { phrase: 'food for thought', severity: 'medium', suggestion: 'worth considering' },
    { phrase: 'in a nutshell', severity: 'medium', suggestion: 'briefly' },
    { phrase: 'last but not least', severity: 'high', suggestion: 'finally' },
    { phrase: 'few and far between', severity: 'high', suggestion: 'rare' },
    { phrase: 'going forward', severity: 'medium', suggestion: 'in the future' },
    { phrase: 'at this point in time', severity: 'high', suggestion: 'now' },
    { phrase: 'meaningful impact', severity: 'medium', suggestion: 'significant effect' },
    { phrase: 'bridge the gap', severity: 'high', suggestion: 'connect' },
]

// Local cliché detection
const detectCliches = (text) => {
    if (!text?.trim()) return []
    const lowerText = text.toLowerCase()
    const detections = []
    const foundPositions = new Set()
    const sortedPatterns = [...CLICHE_PATTERNS].sort((a, b) => b.phrase.length - a.phrase.length)

    for (const pattern of sortedPatterns) {
        let searchIndex = 0
        while (searchIndex < lowerText.length) {
            const index = lowerText.indexOf(pattern.phrase.toLowerCase(), searchIndex)
            if (index === -1) break

            let isOverlapping = false
            for (const pos of foundPositions) {
                if (index >= pos.start && index < pos.end) {
                    isOverlapping = true
                    break
                }
            }

            if (!isOverlapping) {
                foundPositions.add({ start: index, end: index + pattern.phrase.length })
                detections.push({
                    phrase: text.substring(index, index + pattern.phrase.length),
                    reason: `"${pattern.phrase}" is a commonly overused expression.`,
                    suggestion: pattern.suggestion,
                    severity: pattern.severity,
                    position: index,
                    length: pattern.phrase.length,
                })
            }
            searchIndex = index + 1
        }
    }
    return detections.sort((a, b) => a.position - b.position)
}

// Calculate readability score (Flesch-Kincaid)
const calculateReadability = (text) => {
    if (!text?.trim()) return { score: 0, grade: 'N/A', level: 'N/A' }

    const sentences = text.split(/[.!?]+/).filter(s => s.trim())
    const words = text.trim().split(/\s+/).filter(Boolean)
    const syllables = words.reduce((count, word) => {
        return count + countSyllables(word)
    }, 0)

    if (sentences.length === 0 || words.length === 0) {
        return { score: 0, grade: 'N/A', level: 'N/A' }
    }

    // Flesch Reading Ease
    const score = 206.835 - 1.015 * (words.length / sentences.length) - 84.6 * (syllables / words.length)
    const clampedScore = Math.max(0, Math.min(100, score))

    // Grade level
    let grade, level
    if (clampedScore >= 90) { grade = '5th grade'; level = 'Very Easy' }
    else if (clampedScore >= 80) { grade = '6th grade'; level = 'Easy' }
    else if (clampedScore >= 70) { grade = '7th grade'; level = 'Fairly Easy' }
    else if (clampedScore >= 60) { grade = '8-9th grade'; level = 'Standard' }
    else if (clampedScore >= 50) { grade = '10-12th grade'; level = 'Fairly Hard' }
    else if (clampedScore >= 30) { grade = 'College'; level = 'Hard' }
    else { grade = 'Graduate'; level = 'Very Hard' }

    return { score: Math.round(clampedScore), grade, level }
}

const countSyllables = (word) => {
    word = word.toLowerCase().replace(/[^a-z]/g, '')
    if (word.length <= 3) return 1
    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '')
    word = word.replace(/^y/, '')
    const matches = word.match(/[aeiouy]{1,2}/g)
    return matches ? matches.length : 1
}

export default function ProEditor() {
    const [detections, setDetections] = useState([])
    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const [useAI, setUseAI] = useState(true)
    const [rateLimit, setRateLimit] = useState({ allowed: true, remaining: 10, isPro: false })
    const [copied, setCopied] = useState(false)
    const [showShareMenu, setShowShareMenu] = useState(false)
    const [showDownloadMenu, setShowDownloadMenu] = useState(false)
    const [focusMode, setFocusMode] = useState(false)
    const [selectedCliche, setSelectedCliche] = useState(null)
    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 })
    const [, forceUpdate] = useState(0) // Force re-render for undo/redo state
    const debounceRef = useRef(null)
    const editorContainerRef = useRef(null)
    const isApplyingHighlightsRef = useRef(false)
    const previousTextRef = useRef('')

    // Initialize TipTap editor
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
                bulletList: true,
                orderedList: true,
                blockquote: true,
                codeBlock: false,
                code: false,
                horizontalRule: true,
            }),
            Placeholder.configure({
                placeholder: 'Start writing or paste your text here to detect clichés and get AI-powered suggestions...\n\nTry phrases like "think outside the box" or "at the end of the day" to see the magic happen.',
            }),
            CharacterCount,
            Underline,
            Highlight.configure({ multicolor: true }),
            ClicheHighlight,
        ],
        content: '',
        editorProps: {
            attributes: {
                class: 'prose prose-invert prose-lg max-w-none focus:outline-none min-h-[400px] p-6',
            },
            handleClick: (view, pos, event) => {
                // Check if clicked on a cliche mark
                const target = event.target
                if (target.hasAttribute('data-cliche')) {
                    const phrase = target.getAttribute('data-phrase')
                    const suggestion = target.getAttribute('data-suggestion')
                    const severity = target.getAttribute('data-severity')
                    const rect = target.getBoundingClientRect()

                    setSelectedCliche({ phrase, suggestion, severity, element: target })
                    setPopoverPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.bottom + 8
                    })
                    return true
                }
                setSelectedCliche(null)
                return false
            },
        },
        onUpdate: ({ editor }) => {
            // Skip if we're applying highlights (prevents infinite loop)
            if (isApplyingHighlightsRef.current) {
                return
            }

            // Only analyze if actual text content changed (not just formatting)
            const currentText = editor.getText()
            if (currentText === previousTextRef.current) {
                return // Text didn't change, skip analysis
            }
            previousTextRef.current = currentText

            // Clear previous timeout
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
            // Debounce analysis
            debounceRef.current = setTimeout(() => {
                analyzeText(editor)
            }, 800)
        },
        // Force re-render on any transaction to update undo/redo button states
        onTransaction: () => {
            forceUpdate(n => n + 1)
        },
    })

    // Check rate limit on mount
    useEffect(() => {
        const checkLimit = async () => {
            const limit = await checkRateLimitAsync()
            setRateLimit(limit)
        }
        checkLimit()
    }, [])

    // Close menus on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.share-menu') && !e.target.closest('.share-btn')) {
                setShowShareMenu(false)
            }
            if (!e.target.closest('.download-menu') && !e.target.closest('.download-btn')) {
                setShowDownloadMenu(false)
            }
            if (!e.target.closest('.cliche-popover') && !e.target.hasAttribute('data-cliche')) {
                setSelectedCliche(null)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    // Analyze text and apply highlights
    const analyzeText = useCallback(async (editorInstance) => {
        if (!editorInstance) return
        const text = editorInstance.getText()

        if (!text || text.trim().length < 10) {
            setDetections([])
            // Clear all cliche marks
            editorInstance.chain().focus().unsetMark('clicheHighlight').run()
            return
        }

        setIsAnalyzing(true)

        try {
            let results = []

            // Local pattern matching
            const localResults = detectCliches(text)
            results = localResults.map(r => ({ ...r, source: 'local' }))

            // AI detection if enabled
            if (useAI && rateLimit.allowed) {
                try {
                    const aiResults = await detectWithAI(text)
                    if (aiResults.detections?.length > 0) {
                        const localPhrases = new Set(results.map(r => r.phrase.toLowerCase()))
                        const newAiResults = aiResults.detections
                            .filter(r => !localPhrases.has(r.phrase.toLowerCase()))
                            .map(r => ({ ...r, source: 'ai' }))
                        results = [...results, ...newAiResults]
                    }
                    incrementRateLimit()
                    setRateLimit(checkRateLimit())
                } catch (e) {
                    console.error('AI detection error:', e)
                }
            }

            setDetections(results)

            // Apply highlights in editor
            applyHighlights(editorInstance, results, text)

        } catch (error) {
            console.error('Analysis error:', error)
        } finally {
            setIsAnalyzing(false)
        }
    }, [useAI, rateLimit.allowed])

    // Expose setEditorContent globally for tutorial
    useEffect(() => {
        window.setEditorContent = (content) => {
            if (editor) {
                editor.commands.setContent(content)
                // Trigger analysis after loading
                setTimeout(() => analyzeText(editor), 500)
            }
        }
        return () => {
            delete window.setEditorContent
        }
    }, [editor, analyzeText])

    // Apply cliche highlights to editor
    const applyHighlights = (editorInstance, results, text) => {
        if (!editorInstance) return

        // Set flag to prevent onUpdate from triggering re-analysis
        isApplyingHighlightsRef.current = true

        try {
            const tr = editorInstance.state.tr
            tr.setMeta('addToHistory', false)

            // Remove all existing cliche highlights
            tr.removeMark(0, editorInstance.state.doc.content.size, editorInstance.schema.marks.clicheHighlight)

            if (results.length > 0) {
                // Apply new highlights
                const lowerText = text.toLowerCase()

                results.forEach(detection => {
                    const lowerPhrase = detection.phrase.toLowerCase()
                    let pos = 0

                    while ((pos = lowerText.indexOf(lowerPhrase, pos)) !== -1) {
                        const from = pos + 1 // +1 for ProseMirror 1-based positions
                        const to = from + detection.phrase.length

                        const mark = editorInstance.schema.marks.clicheHighlight.create({
                            severity: detection.severity,
                            suggestion: detection.suggestion,
                            phrase: detection.phrase,
                        })

                        tr.addMark(from, to, mark)
                        pos += detection.phrase.length
                    }
                })
            }

            editorInstance.view.dispatch(tr)

        } finally {
            // Reset flag after a short delay to ensure all updates complete
            setTimeout(() => {
                isApplyingHighlightsRef.current = false
            }, 100)
        }
    }

    // Apply suggestion
    const applySuggestion = () => {
        if (!selectedCliche || !editor) return

        const { phrase, suggestion } = selectedCliche
        const text = editor.getText()
        const lowerText = text.toLowerCase()
        const pos = lowerText.indexOf(phrase.toLowerCase())

        if (pos !== -1) {
            editor.chain()
                .setTextSelection({ from: pos + 1, to: pos + 1 + phrase.length })
                .deleteSelection()
                .insertContent(suggestion)
                .run()
        }

        setSelectedCliche(null)

        // Re-analyze after change
        setTimeout(() => analyzeText(editor), 100)
    }

    // Copy text
    const handleCopy = async () => {
        if (!editor) return
        try {
            await navigator.clipboard.writeText(editor.getText())
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }

    // Share handlers
    const handleShare = (platform) => {
        const text = editor?.getText() || ''
        const shareText = `I analyzed my writing with InkSpot and found ${detections.length} areas to improve!\n\n${text.substring(0, 200)}...`
        const encodedText = encodeURIComponent(shareText)
        const encodedUrl = encodeURIComponent(window.location.origin)

        const urls = {
            twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
            email: `mailto:?subject=${encodeURIComponent('InkSpot Analysis Results')}&body=${encodedText}%0A%0A${encodedUrl}`,
        }

        if (urls[platform]) {
            window.open(urls[platform], '_blank', 'width=600,height=400')
        }
        setShowShareMenu(false)
    }

    // Download handlers
    const handleDownload = (format) => {
        const text = editor?.getText() || ''
        const html = editor?.getHTML() || ''
        const dateStr = new Date().toISOString().split('T')[0]

        // PDF Download
        if (format === 'pdf') {
            const element = document.createElement('div')
            element.innerHTML = `
                <div style="font-family: Arial, sans-serif; padding: 40px; color: #000;">
                    <h1 style="color: #333; margin-bottom: 20px;">InkSpot Writing Export</h1>
                    <div style="line-height: 1.6;">${html}</div>
                    <div style="margin-top: 50px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 12px; color: #888;">
                        Generated by InkSpot on ${new Date().toLocaleString()}
                    </div>
                </div>
            `
            const opt = {
                margin: 10,
                filename: `inkspot-export-${dateStr}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            }
            html2pdf().set(opt).from(element).save()
            setShowDownloadMenu(false)
            return
        }

        // Word Download (DOC/DOCX compatible HTML)
        if (format === 'docx') {
            const header = `
                <html xmlns:o='urn:schemas-microsoft-com:office:office' 
                      xmlns:w='urn:schemas-microsoft-com:office:word' 
                      xmlns='http://www.w3.org/TR/REC-html40'>
                <head><meta charset='utf-8'><title>Export HTML to Word Document with JavaScript</title></head><body>
            `
            const footer = "</body></html>"
            const sourceHTML = header + html + footer
            const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML)

            const link = document.createElement("a")
            document.body.appendChild(link)
            link.href = source
            link.download = `inkspot-export-${dateStr}.doc`
            link.click()
            document.body.removeChild(link)
            setShowDownloadMenu(false)
            return
        }

        // Text & Markdown
        const report = generateReport(text, detections)
        let content, mimeType, extension

        switch (format) {
            case 'txt':
                content = report
                mimeType = 'text/plain'
                extension = 'txt'
                break
            case 'md':
                content = report.replace(/^(.*)\n={4,}/gm, '# $1').replace(/^(.*)\n-{4,}/gm, '## $1')
                mimeType = 'text/markdown'
                extension = 'md'
                break
            default:
                return
        }

        const blob = new Blob([content], { type: mimeType })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `inkspot-analysis-${dateStr}.${extension}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        setShowDownloadMenu(false)
    }

    const generateReport = (text, detections) => {
        return `InkSpot Writing Analysis Report
================================
Generated: ${new Date().toLocaleString()}

SUMMARY
-------
Words: ${text.trim().split(/\s+/).filter(Boolean).length}
Characters: ${text.length}
Issues Found: ${detections.length}

DETECTED ISSUES
---------------
${detections.length === 0 ? 'No issues detected. Great writing!' :
                detections.map((d, i) => `
${i + 1}. "${d.phrase}" [${d.severity?.toUpperCase()}]
   Suggestion: ${d.suggestion}
`).join('')}

---
Analyzed with InkSpot - AI Writing Assistant
${window.location.origin}
`
    }

    // Stats
    const text = editor?.getText() || ''
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length
    const charCount = text.length
    const readability = calculateReadability(text)
    const readingTime = Math.ceil(wordCount / 200) // avg 200 wpm

    // Severity counts
    const highCount = detections.filter(d => d.severity === 'high').length
    const mediumCount = detections.filter(d => d.severity === 'medium').length
    const lowCount = detections.filter(d => d.severity === 'low').length

    return (
        <div className={`flex flex-col min-h-full transition-all duration-300 ${focusMode ? 'fixed inset-0 z-50 bg-slate-950 p-6' : ''}`}>
            {/* Formatting Toolbar - Word-style */}
            <div className="flex flex-wrap items-center gap-1 mb-3 pb-3 border-b border-white/10">
                {/* Undo/Redo */}
                <div className="flex items-center gap-0.5 mr-2">
                    <button
                        onClick={() => editor?.chain().focus().undo().run()}
                        disabled={!editor?.can().undo()}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Undo (Ctrl+Z)"
                    >
                        <Undo className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().redo().run()}
                        disabled={!editor?.can().redo()}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Redo (Ctrl+Y)"
                    >
                        <Redo className="w-4 h-4" />
                    </button>
                </div>

                <div className="w-px h-6 bg-white/10" />

                {/* Text Formatting */}
                <div className="flex items-center gap-0.5 mx-2">
                    <button
                        onClick={() => editor?.chain().focus().toggleBold().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('bold') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Bold (Ctrl+B)"
                    >
                        <Bold className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleItalic().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('italic') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Italic (Ctrl+I)"
                    >
                        <Italic className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleUnderline().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('underline') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Underline (Ctrl+U)"
                    >
                        <UnderlineIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleStrike().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('strike') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Strikethrough"
                    >
                        <Strikethrough className="w-4 h-4" />
                    </button>
                </div>

                <div className="w-px h-6 bg-white/10" />

                {/* Headings */}
                <div className="flex items-center gap-0.5 mx-2">
                    <button
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('heading', { level: 1 }) ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Heading 1"
                    >
                        <Heading1 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('heading', { level: 2 }) ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Heading 2"
                    >
                        <Heading2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('heading', { level: 3 }) ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Heading 3"
                    >
                        <Heading3 className="w-4 h-4" />
                    </button>
                </div>

                <div className="w-px h-6 bg-white/10" />

                {/* Lists & Quote */}
                <div className="flex items-center gap-0.5 mx-2">
                    <button
                        onClick={() => editor?.chain().focus().toggleBulletList().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('bulletList') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Bullet List"
                    >
                        <List className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('orderedList') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Numbered List"
                    >
                        <ListOrdered className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                        className={`p-2 rounded-lg transition-all ${editor?.isActive('blockquote') ? 'bg-primary-500/30 text-primary-400' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        title="Quote"
                    >
                        <Quote className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => editor?.chain().focus().setHorizontalRule().run()}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all"
                        title="Horizontal Rule"
                    >
                        <Minus className="w-4 h-4" />
                    </button>
                </div>

                <div className="w-px h-6 bg-white/10" />

                {/* Clear Formatting */}
                <button
                    onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}
                    className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all ml-2"
                    title="Clear Formatting"
                >
                    <Type className="w-4 h-4" />
                </button>
            </div>

            {/* Analysis Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    {/* Analyze Button */}
                    <button
                        onClick={() => editor && analyzeText(editor)}
                        disabled={isAnalyzing || !text}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500/20 to-accent-500/20 border border-primary-500/30 text-primary-400 hover:from-primary-500/30 hover:to-accent-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-tutorial="analyze-btn"
                    >
                        {isAnalyzing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Wand2 className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">Analyze</span>
                    </button>

                    {/* AI Toggle */}
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <div className="relative">
                            <input
                                type="checkbox"
                                checked={useAI}
                                onChange={(e) => setUseAI(e.target.checked)}
                                className="sr-only"
                            />
                            <div className={`w-10 h-6 rounded-full transition-colors duration-200 ${useAI ? 'bg-primary-500' : 'bg-white/20'}`}>
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-200 ${useAI ? 'translate-x-5' : 'translate-x-1'}`} />
                            </div>
                        </div>
                        <span className="text-sm text-white/70 group-hover:text-white/90 transition-colors">
                            AI-Enhanced
                        </span>
                        {useAI && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${rateLimit.isPro ? 'bg-primary-500/20 text-primary-400' : 'bg-white/10 text-white/50'}`}>
                                {rateLimit.isPro ? '∞' : `${rateLimit.remaining} left`}
                            </span>
                        )}
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    {/* Copy */}
                    <button
                        onClick={handleCopy}
                        disabled={!text}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
                        title="Copy text"
                    >
                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>

                    {/* Share */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowShareMenu(!showShareMenu); setShowDownloadMenu(false) }}
                            disabled={!text}
                            className="share-btn p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
                            title="Share"
                        >
                            <Share2 className="w-4 h-4" />
                        </button>
                        {showShareMenu && (
                            <div className="share-menu absolute right-0 top-full mt-2 w-48 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                                <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <Twitter className="w-4 h-4 text-[#1DA1F2]" /> Twitter/X
                                </button>
                                <button onClick={() => handleShare('linkedin')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <Linkedin className="w-4 h-4 text-[#0A66C2]" /> LinkedIn
                                </button>
                                <button onClick={() => handleShare('facebook')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <Facebook className="w-4 h-4 text-[#1877F2]" /> Facebook
                                </button>
                                <button onClick={() => handleShare('email')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <Mail className="w-4 h-4 text-white/60" /> Email
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Download */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowDownloadMenu(!showDownloadMenu); setShowShareMenu(false) }}
                            disabled={!text}
                            className="download-btn p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
                            title="Download"
                        >
                            <Download className="w-4 h-4" />
                        </button>
                        {showDownloadMenu && (
                            <div className="download-menu absolute right-0 top-full mt-2 w-56 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                                <button onClick={() => handleDownload('pdf')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <FileText className="w-4 h-4 text-red-400" /> Export as PDF
                                </button>
                                <button onClick={() => handleDownload('docx')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <FileType className="w-4 h-4 text-blue-400" /> Export as Word (.doc)
                                </button>
                                <div className="h-px bg-white/10 my-1" />
                                <button onClick={() => handleDownload('txt')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <FileText className="w-4 h-4 text-white/60" /> Plain Text Report
                                </button>
                                <button onClick={() => handleDownload('md')} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 transition-colors">
                                    <FileDown className="w-4 h-4 text-purple-400" /> Markdown Report
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="w-px h-6 bg-white/10 mx-1" />

                    {/* Focus Mode Toggle */}
                    <button
                        onClick={() => setFocusMode(!focusMode)}
                        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all"
                        title={focusMode ? 'Exit focus mode' : 'Focus mode'}
                    >
                        {focusMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex flex-1 gap-6 overflow-hidden">
                {/* Editor */}
                <div className="flex-1 flex flex-col min-w-0">
                    <div
                        ref={editorContainerRef}
                        className="relative flex-1 rounded-xl bg-white/5 border border-white/10 overflow-hidden focus-within:border-primary-400/50 focus-within:ring-2 focus-within:ring-primary-400/20 transition-all"
                        data-tutorial="editor"
                    >
                        <EditorContent
                            editor={editor}
                            className="h-full overflow-y-auto custom-scrollbar"
                        />

                        {/* Analyzing indicator */}
                        {isAnalyzing && (
                            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-400 text-sm">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Analyzing...
                            </div>
                        )}
                    </div>

                    {/* Bottom Stats Bar */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2 text-sm text-white/50">
                                <Type className="w-4 h-4" />
                                <span>{wordCount} words</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-white/50">
                                <span>{charCount} chars</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-white/50">
                                <Clock className="w-4 h-4" />
                                <span>{readingTime} min read</span>
                            </div>
                        </div>

                        {/* Issue Legend */}
                        <div className="flex items-center gap-4">
                            {highCount > 0 && (
                                <div className="flex items-center gap-1.5 text-sm">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                    <span className="text-red-400">{highCount}</span>
                                </div>
                            )}
                            {mediumCount > 0 && (
                                <div className="flex items-center gap-1.5 text-sm">
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                                    <span className="text-yellow-400">{mediumCount}</span>
                                </div>
                            )}
                            {lowCount > 0 && (
                                <div className="flex items-center gap-1.5 text-sm">
                                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                                    <span className="text-blue-400">{lowCount}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Sidebar - Stats & Results */}
                {!focusMode && (
                    <div className="w-80 flex-shrink-0 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
                        {/* Writing Stats Card */}
                        <div className="glass-card p-5" data-tutorial="stats">
                            <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-primary-400" />
                                Writing Stats
                            </h3>

                            {/* Readability Score */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-white/60">Readability</span>
                                    <span className="text-sm font-medium text-white">{readability.level}</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-500"
                                        style={{ width: `${readability.score}%` }}
                                    />
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span className="text-xs text-white/40">Hard</span>
                                    <span className="text-xs text-white/40">{readability.grade}</span>
                                    <span className="text-xs text-white/40">Easy</span>
                                </div>
                            </div>

                            {/* Quick Stats Grid */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-white/5">
                                    <div className="text-lg font-bold text-white">{wordCount}</div>
                                    <div className="text-xs text-white/50">Words</div>
                                </div>
                                <div className="p-3 rounded-lg bg-white/5">
                                    <div className="text-lg font-bold text-white">{readingTime}</div>
                                    <div className="text-xs text-white/50">Min Read</div>
                                </div>
                            </div>
                        </div>

                        {/* Issues Card */}
                        <div className="glass-card p-5 flex-1 flex flex-col min-h-0" data-tutorial="results">
                            <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                Issues Found
                                {detections.length > 0 && (
                                    <span className="ml-auto px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                                        {detections.length}
                                    </span>
                                )}
                            </h3>

                            {detections.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                                    </div>
                                    <h4 className="text-lg font-semibold text-white mb-2">Looking Good!</h4>
                                    <p className="text-sm text-white/50">
                                        No clichés detected in your text. Keep writing to get real-time feedback.
                                    </p>
                                </div>
                            ) : (
                                <div className="flex-1 overflow-y-auto space-y-2 custom-scrollbar">
                                    {detections.map((detection, index) => (
                                        <div
                                            key={index}
                                            className={`p-3 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${detection.severity === 'high'
                                                ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20'
                                                : detection.severity === 'medium'
                                                    ? 'bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20'
                                                    : 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20'
                                                }`}
                                            onClick={() => {
                                                // Find and highlight in editor
                                                if (editor) {
                                                    const text = editor.getText()
                                                    const pos = text.toLowerCase().indexOf(detection.phrase.toLowerCase())
                                                    if (pos !== -1) {
                                                        editor.chain()
                                                            .setTextSelection({ from: pos + 1, to: pos + 1 + detection.phrase.length })
                                                            .scrollIntoView()
                                                            .run()
                                                    }
                                                }
                                            }}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="text-sm font-medium text-white">"{detection.phrase}"</span>
                                                {detection.source === 'ai' && (
                                                    <Sparkles className="w-3.5 h-3.5 text-primary-400 flex-shrink-0" />
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-xs text-primary-400">→</span>
                                                <span className="text-xs text-white/70">{detection.suggestion}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Cliche Fix Popover */}
            {selectedCliche && (
                <div
                    className="cliche-popover fixed z-50 bg-slate-800 border border-white/20 rounded-xl shadow-2xl p-4 max-w-xs"
                    style={{
                        left: Math.min(popoverPosition.x, window.innerWidth - 280),
                        top: popoverPosition.y,
                        transform: 'translateX(-50%)',
                    }}
                >
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                            <div className="text-xs text-white/50 mb-1">Replace cliché</div>
                            <div className="text-sm font-medium text-white">"{selectedCliche.phrase}"</div>
                        </div>
                        <button
                            onClick={() => setSelectedCliche(null)}
                            className="p-1 rounded-lg hover:bg-white/10 text-white/50 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <button
                        onClick={applySuggestion}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-400 text-white text-sm font-medium transition-colors"
                    >
                        <Check className="w-4 h-4" />
                        Use "{selectedCliche.suggestion}"
                    </button>
                </div>
            )}
        </div>
    )
}
